import { useState } from "react";
import { MessageSquarePlus, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CATEGORIES = [
  { value: "WRONG_ANSWER", label: "Wrong answer" },
  { value: "QUESTION_ERROR", label: "Error / typo in question" },
  { value: "SOLUTION_UNCLEAR", label: "Solution unclear or wrong" },
  { value: "DIAGRAM_ISSUE", label: "Diagram / image issue" },
  { value: "OTHER", label: "Other" },
];

export const QuestionFeedback = ({ questionId }) => {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!category) {
      toast.warning("Pick what the feedback is about");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/pyq/${questionId}/feedback`, { category, comment });
      setSent(true);
      setOpen(false);
      toast.success("Thanks! Your feedback was sent to the team.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send feedback");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground" data-testid="feedback-sent">
        <Check className="h-4 w-4 text-green-500" /> Feedback sent — thank you!
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="open-feedback-btn"
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
      >
        <MessageSquarePlus className="h-4 w-4" /> Report an issue / give feedback
      </button>
    );
  }

  return (
    <div className="mt-4 rounded-xl border border-border bg-secondary/30 p-4" data-testid="feedback-form">
      <p className="mb-3 text-sm font-semibold">Tell us what's wrong with this question or its solution</p>
      <Select value={category} onValueChange={setCategory}>
        <SelectTrigger data-testid="feedback-category" className="rounded-xl"><SelectValue placeholder="What's it about?" /></SelectTrigger>
        <SelectContent>
          {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Textarea
        data-testid="feedback-comment"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Add details (optional)…"
        rows={3}
        className="mt-3 rounded-xl"
      />
      <div className="mt-3 flex gap-2">
        <Button onClick={submit} disabled={busy} data-testid="submit-feedback-btn" size="sm" className="rounded-full">
          Send feedback
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
};
